import React, { useCallback, useEffect, useState } from 'react'
import { Badge, Spinner } from 'flowbite-react'
import toast from 'react-hot-toast'
import { toastApiError } from '../utils/apiError'
import { deleteLead, fetchLeadsForDomain } from '../utils/leads'
import TrashIconButton from './TrashIconButton'

const statusColor = (status) => {
  const value = String(status ?? '').toLowerCase()
  if (['completed', 'complete'].includes(value)) return 'success'
  if (['failed', 'error'].includes(value)) return 'failure'
  if (['processing', 'pending', 'in_progress'].includes(value)) return 'warning'
  return 'gray'
}

const RelatedLeadsTable = ({ token, domain }) => {
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  const domainLabel = String(domain ?? '').trim()

  const fetchLeads = useCallback(async () => {
    if (!domainLabel) {
      setLeads([])
      return
    }

    setLoading(true)
    try {
      const items = await fetchLeadsForDomain(token, domain)
      setLeads(items)
    } catch (error) {
      toastApiError(error, 'Failed to load related leads')
      setLeads([])
    } finally {
      setLoading(false)
    }
  }, [token, domain, domainLabel])

  useEffect(() => {
    fetchLeads()
  }, [fetchLeads])

  const handleDelete = async (lead, event) => {
    event?.stopPropagation?.()
    if (
      !window.confirm(
        `Delete lead for ${lead.email} (${lead.domain})? This cannot be undone.`
      )
    ) {
      return
    }

    setDeletingId(lead.id)
    try {
      await deleteLead(token, lead.id)
      const nextLeads = leads.filter((item) => item.id !== lead.id)
      setLeads(nextLeads)
      toast.success('Lead deleted')
    } catch (error) {
      toastApiError(error, 'Failed to delete lead')
    } finally {
      setDeletingId(null)
    }
  }

  if (!domainLabel) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Add an official domain to see related leads for this city.
      </p>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700/60">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Domain
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Created
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <span className="sr-only">Delete</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
            {leads.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  No leads found for{' '}
                  <span className="font-mono text-gray-600 dark:text-gray-300">
                    {domainLabel}
                  </span>
                  .
                </td>
              </tr>
            ) : (
              leads.map((lead) => (
                <tr
                  key={lead.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/40"
                >
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    {lead.email}
                  </td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-600 dark:text-gray-400">
                    {lead.domain}
                  </td>
                  <td className="px-4 py-3">
                    <Badge color={statusColor(lead.status)}>{lead.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {lead.date_created
                      ? new Date(lead.date_created).toLocaleString()
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <TrashIconButton
                      label={`Delete lead for ${lead.email}`}
                      loading={deletingId === lead.id}
                      disabled={Boolean(deletingId && deletingId !== lead.id)}
                      onClick={(event) => handleDelete(lead, event)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {leads.length > 0 && (
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
          {leads.length} lead{leads.length === 1 ? '' : 's'} for{' '}
          <span className="font-mono text-gray-600 dark:text-gray-300">{domainLabel}</span>
        </div>
      )}
    </div>
  )
}

export default RelatedLeadsTable
